/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react';
import { useResolution886Report } from '@/context/Resolution886Report';
import { useCreateUpdatePreventiveMeasure, useGetPreventiveMeasure } from '@/hooks/v2/resolution-886';
import { useStepControl } from './useStepControl';

type Measure = {
	result: string;
	observations?: string;
};

type Measures = Record<string, Measure>;

export function useActionPlanStep() {
	const { handleUpdateStep } = useStepControl();
	const { form, report } = useResolution886Report();

	const organization_id = form.getFieldValue(['general_information', 'organization_id']);
	const company_id = form.getFieldValue(['general_information', 'company_id']);
	const review_id = form.getFieldValue(['general_information', 'review_id']);

	const { mutate: createUpdatePreventiveMeasure, isLoading: isLoadingCreateUpdatePreventiveMeasure } =
		useCreateUpdatePreventiveMeasure();

	const { data: preventiveMeasure, isFetching: isLoadingGetPreventiveMeasure } = useGetPreventiveMeasure({
		organization_id,
		company_id,
		review_id,
		report_id: report!.id
	});

	function observationsTrimmed(observations: string | undefined): string | null {
		const observations_trimmed = observations ? String(observations).trim() : null;
		return observations_trimmed !== '' ? observations_trimmed : null;
	}

	async function handleSavePreventiveMeasures(): Promise<void> {
		await form.validateFields();

		const { measures } = form.getFieldsValue(['measures']);

		const transformed = Object.entries(measures as Measures)?.map(([key, value]) => ({
			preventive_measure_key: key,
			preventive_measure_result: value.result?.toUpperCase(),
			preventive_measure_description: observationsTrimmed(value.observations)
		}));

		const payload = {
			organization_id,
			company_id,
			review_id,
			report_id: report!.id,
			preventive_measures_results: transformed
		};

		createUpdatePreventiveMeasure(payload, {
			onSuccess: () => {
				handleUpdateStep(4);
			}
		});
	}

	useEffect(() => {
		if (preventiveMeasure && preventiveMeasure.length) {
			preventiveMeasure[0].preventive_measure_results.forEach((item) => {
				form.setFieldsValue({
					measures: {
						[item.preventive_measure.key]: {
							result: String(item.value).toLowerCase(),
							observations: item.description || null
						}
					}
				});
			});
		}
	}, [preventiveMeasure]);

	const isLoading = isLoadingCreateUpdatePreventiveMeasure || isLoadingGetPreventiveMeasure;

	return { isLoading, handleSavePreventiveMeasures, handleUpdateStep };
}
